import React from 'react'
import { useSelector } from 'react-redux'
import { round } from 'lodash'

import { getUserName, getPositionName, getProjectName, getProjectCode, getTagName } from 'services/Utilities'
import { Format } from 'services/Dates'
import { toggleResourceSelection } from 'store/actions/tables'
import { DateTime, AutomaticButtonGroup } from 'components/UI'
import RowErrorBoundary from 'components/UI/Table/RowErrorBoundary'

import { Spinner } from 'ui'
import { t } from 'translations'

const TableBody = ({ name, options, rowActions, showFiltersChangedMessage, triggerSearch }) => {
  const { defaultTimeZone: timeZone } = useSelector(state => state.settings)
  const { usersById } = useSelector(state => state.resources)
  const { alwaysVisible, isLoading, content, dataKey, header, limit, page, asyncContent } = options
  const contentToDisplay = asyncContent ? content : content.slice((page - 1) * limit, page * limit)

  const format = (header, value, row, options) => {
    if (typeof header.noValue !== 'undefined' && typeof value === 'undefined') return header.noValue

    switch (header.type) {
      case 'boolean':
        return value ? (
          <div className="text-center">
            <i className="fa fa-far fa-check" />
          </div>
        ) : (
          ''
        )
      case 'image':
        if (!value) return ''
        return <img src={`${header.prefix}${value}${header.suffix}`} alt={value} height={header.height} />
      case 'custom':
        return header.render(value, row, options)
      case 'minutesToHours':
        return value ? `${round(value / 60, 2)} ${t('components.table.HOURS')}` : null
      case 'minutesToDays':
        return value ? `${round(value / 60 / 8, 2)} ${t('components.table.DAYS')}` : null
      case 'userId':
        return (
          <div>
            {getUserName(value, header)} <div className="text-danger font-weight-bold">{usersById[value]?.archived ? t('manager.users.ARCHIVED') : null}</div>
          </div>
        )
      case 'positionId':
        return getPositionName(value, { canBeNull: true, noValue: '' })
      case 'projectId':
        if (value) {
          return (
            <>
              <div>{getProjectName(value, { canBeNull: true })}</div>
              <small className="text-muted">[{getProjectCode(value, { canBeNull: false })}]</small>
            </>
          )
        } else {
          return <div>{getProjectName(value, { canBeNull: true, noValue: '-' })}</div>
        }
      case 'tagId':
        return getTagName(value, { canBeNull: true, noValue: '' })
      case 'date':
        return value ? <DateTime {...header} date={value} timeZone={timeZone} /> : null
      // Renders array values as is with commas between values
      case 'unrenderedArray':
        return Array.isArray(value) ? value.join(', ') : null
      // Passes values through provided render function and adds commas between values
      case 'renderedArray':
        return Array.isArray(value) ? value.map(v => header.render(v, row, options)).join(', ') : null
      case 'translate':
        return t(`${header.module ? `${header.module}.` : ''}${value.toUpperCase()}`)
      // Handles created/edited/updatedAt and by rendering
      case 'dateBy': {
        const { [header.dateKey]: date, [header.byKey]: by } = row

        if (!date) return null

        return (
          <div className="d-flex flex-column text-nowrap">
            <div>{Format(date, { outFormat: 'humanDateTime', isUTC: true, timeZone })}</div>
            {by ? (
              <div>
                <small className="text-muted">{getUserName(by)}</small>
              </div>
            ) : null}
          </div>
        )
      }
      case 'currency': {
        return `$ ${(value || 0).toFixed(2)}`
      }
      default:
        break
    }
  }

  const colSpanLength = () => {
    let length = header.length
    if (rowActions) length++
    if (options.isSelectable) length++
    return length
  }

  const buildOptions = row => (
    <td className="table-sticky-right">
      <AutomaticButtonGroup>
        {/* Clone the elements to add the row information to each actions passed */}
        {rowActions.map((action, index) => {
          if (!action) return null
          const renderedAction = typeof action === 'function' ? action(row, options) : action
          if (!renderedAction) return null // If the function returns null
          return React.cloneElement(renderedAction, {
            onClick: (e, ...props) => {
              if (e) e.stopPropagation()
              if (renderedAction.props.onClick) renderedAction.props.onClick(row, options, ...props)
            },
            row,
            key: `${renderedAction.props.title || index}-${row[dataKey]}`
          })
        })}
      </AutomaticButtonGroup>
    </td>
  )

  if (showFiltersChangedMessage)
    return (
      <tbody>
        <tr>
          <td colSpan={colSpanLength()}>
            <div className={'d-flex flex-row align-items-center'}>
              <i className="far fa-arrows-rotate mr-2"></i> {t('components.table.FILTERS_HAVE_CHANGED')}
            </div>
            <small onClick={() => triggerSearch()} className="text-link text-primary">
              {' '}
              {t('components.table.SEARCH_LINK')}
            </small>
          </td>
        </tr>
      </tbody>
    )

  return (
    <tbody>
      {isLoading ? (
        /* If no elements, show a spinner */
        <RowErrorBoundary>
          <tr>
            <td colSpan={colSpanLength()}>
              <Spinner size="lg" /> {t('components.table.LOADING_ITEMS')}
            </td>
          </tr>
        </RowErrorBoundary>
      ) : (
        contentToDisplay.map(b => (
          <RowErrorBoundary key={b[dataKey]} _id={b[dataKey]}>
            <tr className={b.advancedClass}>
              {options.isSelectable ? (
                <td className=" d-md-table-cell">
                  <button
                    className={`btn btn-sm ${options.selectedResourcesMap[b[dataKey]] ? 'btn-success' : 'btn-outline-secondary'}`}
                    onClick={() => toggleResourceSelection(name, b[dataKey])}
                  >
                    <i className={`${options.selectedResourcesMap[b[dataKey]] ? 'fas fa-check-circle' : 'far fa-circle'}`} />
                  </button>
                </td>
              ) : null}
              {header.map(h => {
                // Do not want to see this column
                if (!alwaysVisible && !h.notRemovable && !options.visibleColumn[h.key]) return null
                const parsedValue = h.type ? format(h, b[h.key], b, options) : b[h.key]
                return (
                  <td key={`${b[dataKey]}-${h.key}`} className={h.class || ''}>
                    <div className={h.noMaxWidth ? '' : 'merinio-table-cell'}>
                      {parsedValue}
                      {h.defaultValue && !parsedValue ? h.defaultValue : null}
                      {h.children ? <div className="pt-2">{h.children(b, h)}</div> : null}
                    </div>
                  </td>
                )
              })}
              {rowActions ? buildOptions(b) : null}
            </tr>
            {/* Content section, accessible when clicking on the row (and when the component is provided) */}
            {b.active && options.detailsMap[b._id] ? (
              <tr className="table-active">
                <td colSpan={colSpanLength()}>{React.cloneElement(options.detailsMap[b._id])}</td>
              </tr>
            ) : null}
          </RowErrorBoundary>
        ))
      )}
      {!isLoading && !contentToDisplay.length ? (
        <tr>
          <td colSpan={colSpanLength()}>
            <i className="far fa-empty-set"></i> {page === 1 ? t('components.table.NOTHING_TO_DISPLAY') : t('components.table.NOTHING_ON_THIS_PAGE')}
          </td>
        </tr>
      ) : null}
    </tbody>
  )
}

export default TableBody
